import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, MailOpen, MousePointerClick, AlertTriangle,
  Loader2, ArrowUpRight, ArrowDownRight, RefreshCw
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { getEmailAnalytics, getSettings } from '@/lib/api';

export default function AdminEmailAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [webhookSecret, setWebhookSecret] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([getEmailAnalytics(), getSettings()])
      .then(([analytics, settings]) => {
        setData(analytics);
        setWebhookSecret(settings?.brevo_webhook_secret || '');
      })
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <AdminLayout title="Email Analytics">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-rtb-purple animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const s = data?.summary || {};

  const cards = [
    { label: 'Delivered', value: s.delivered || 0, icon: Mail, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { label: 'Opened', value: s.opened || 0, sub: `${s.open_rate || 0}%`, icon: MailOpen, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { label: 'Clicked', value: s.clicked || 0, sub: `${s.click_rate || 0}%`, icon: MousePointerClick, color: 'text-violet-400', bg: 'bg-violet-500/10' },
    { label: 'Bounced', value: s.bounced || 0, sub: `${s.bounce_rate || 0}%`, icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  ];

  const eventColors = {
    delivered: 'text-blue-400',
    opened: 'text-emerald-400',
    unique_opened: 'text-emerald-300',
    click: 'text-violet-400',
    unique_click: 'text-violet-300',
    hard_bounce: 'text-red-400',
    soft_bounce: 'text-amber-400',
    spam: 'text-red-500',
    request: 'text-white/40',
  };

  return (
    <AdminLayout title="Email Analytics">
      <div className="space-y-6">
        {/* Refresh */}
        <div className="flex justify-between items-center">
          <p className="text-white/40 text-sm">Brevo email & SMS delivery tracking</p>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="border-white/10 text-white/60 hover:text-white rounded-lg"
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Webhook URL notice */}
        {(() => {
          const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/brevo`;
          const fullUrl = webhookSecret ? `${baseUrl}?token=${webhookSecret}` : baseUrl;
          return (
            <div className="p-4 bg-rtb-purple/5 border border-rtb-purple/20 rounded-xl space-y-2">
              <p className="text-white/60 text-sm">
                <span className="text-rtb-purple font-bold">Webhook URL (with secret token):</span>
              </p>
              <div className="flex items-center gap-2">
                <code className="text-white/80 bg-white/5 px-3 py-1.5 rounded text-xs flex-1 overflow-x-auto" data-testid="brevo-webhook-url">
                  {fullUrl}
                </code>
                <button
                  onClick={() => {
                    const el = document.createElement('textarea');
                    el.value = fullUrl;
                    el.style.position = 'fixed';
                    el.style.opacity = '0';
                    document.body.appendChild(el);
                    el.select();
                    document.execCommand('copy');
                    document.body.removeChild(el);
                    toast.success('Copied!');
                  }}
                  className="px-3 py-1.5 bg-rtb-purple/10 text-rtb-purple text-xs rounded-lg hover:bg-rtb-purple/20 flex-shrink-0"
                  data-testid="copy-brevo-webhook-url"
                >
                  Copy
                </button>
              </div>
              <p className="text-white/30 text-xs">Paste this URL in your Brevo account under Settings → Webhooks. The <code>?token=</code> parameter authenticates incoming events.</p>
            </div>
          );
        })()}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="p-5 bg-white/[0.04] border border-white/[0.06] rounded-xl"
              data-testid={`stat-${c.label.toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-9 h-9 ${c.bg} flex items-center justify-center rounded-lg`}>
                  <c.icon className={`w-4.5 h-4.5 ${c.color}`} />
                </div>
                {c.sub && (
                  <span className={`text-sm font-bold ${parseFloat(c.sub) > 0 ? c.color : 'text-white/30'}`}>
                    {c.sub}
                  </span>
                )}
              </div>
              <p className="text-2xl font-oswald font-bold text-white">{c.value.toLocaleString()}</p>
              <p className="text-white/40 text-xs mt-1">{c.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Recent Events Feed */}
        <div className="p-6 bg-white/[0.04] border border-white/[0.06] rounded-xl">
          <h3 className="font-oswald text-lg font-bold text-white mb-4">Recent Events</h3>
          {(!data?.recent_events || data.recent_events.length === 0) ? (
            <div className="text-center py-12">
              <Mail className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/40">No events yet</p>
              <p className="text-white/20 text-sm">Events will appear here once Brevo starts sending webhook data.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {data.recent_events.map((ev, i) => (
                <motion.div
                  key={ev.id || i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.02 }}
                  className="flex items-center gap-4 p-3 bg-white/[0.02] rounded-lg"
                  data-testid={`event-${ev.id}`}
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    ev.event === 'delivered' ? 'bg-blue-400' :
                    ev.event?.includes('open') ? 'bg-emerald-400' :
                    ev.event?.includes('click') ? 'bg-violet-400' :
                    ev.event?.includes('bounce') ? 'bg-amber-400' :
                    ev.event === 'spam' ? 'bg-red-500' : 'bg-white/20'
                  }`} />
                  <span className={`text-sm font-mono w-28 flex-shrink-0 ${eventColors[ev.event] || 'text-white/40'}`}>
                    {ev.event}
                  </span>
                  <span className="text-white/60 text-sm truncate flex-1">{ev.email}</span>
                  <span className="text-white/20 text-xs flex-shrink-0">
                    {ev.subject ? ev.subject.substring(0, 30) + '...' : ''}
                  </span>
                  <span className="text-white/20 text-xs flex-shrink-0">
                    {ev.created_at?.substring(0, 16).replace('T', ' ')}
                  </span>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
